import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tooltip, Tag } from 'antd';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAbility } from '@/ability/AbilityProvider';
import { parsePermission, PROJECT_CLIENT_VIEW } from '@/ability';
import { useProjects, PROJECTS_QUERY_KEY } from './useProjects';
import StatusBadge from '@/components/ui/StatusBadge';
import { asCurrency } from '@/helpers/format';
import { getInitials, getAvatarColor, buildProjectsListQueryParams } from './projectHelpers';
import { AvatarStackWrap, AvatarStackCircle, AvatarStackOverflow } from '@/pages/inside/tenant/style';
import { PRIORITY_OPTIONS } from './useCreateProjectModal';
import { getProjectTemplates, getPublicCategories, reorderStarredProjects, reorderProjects, updateProjectSettings } from '@services/projects.service';
import { getClients } from '@services/clients.service';
import { useAuthStore } from '@stores/auth.store';
import { message } from 'antd';

const projectDetailPath = (projectId, pathname) =>
    pathname?.startsWith('/tenant') ? `/tenant/projects/${projectId}` : `/projects/${projectId}`;

const STATUS_TAB = { ALL: 'all', STARRED: 'starred', ACTIVE: 'active', PENDING: 'pending_completion', INACTIVE: 'inactive' };

/** Unfiltered snapshot for KPI row (same cap as list). */
const PROJECTS_KPI_QUERY_PARAMS = { page: 1, limit: 100 };

const PRIORITY_OPTIONS_FOR_FILTER = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
];

const CLIENT_TYPE_OPTIONS = [
    { value: 'my past clients', label: 'My Past Clients' },
    { value: 'platform client', label: 'Platform Client' },
    { value: 'out of the platform', label: 'Out of the Platform' },
    { value: 'own product', label: 'Own Product' },
];

const PROJECT_TYPE_OPTIONS = [
    { value: 'fixed_sdlc', label: 'Fixed SDLC' },
    { value: 'hourly_resource_augment', label: 'Hourly Resource Augment' },
    { value: 'fixed_recurring_resource_augment', label: 'Fixed Recurring Resource Augment' },
    { value: 'in_house_internal', label: 'In House Internal' },
];

const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

function toTitleWords(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return '—';
    return s
        .replace(/_/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

function formatClientType(type) {
    const t = String(type ?? '').trim().toLowerCase();
    if (t === 'in_house') return 'In House';
    if (t === 'managed') return 'Managed';
    return toTitleWords(t);
}

export const useProjectsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const ability = useAbility();
    const userId = useAuthStore((s) => s.user?.id ?? s.user?._id ?? null);

    const canViewClients = useMemo(() => {
        const parsed = parsePermission(PROJECT_CLIENT_VIEW);
        if (!parsed) return false;
        return ability?.can(parsed[0], parsed[1]);
    }, [ability]);

    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const debounceTimeoutRef = useRef(null);
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    
    // Priority filter state
    const [filterPriority, setFilterPriority] = useState(undefined);
    const [filterPriorityDraft, setFilterPriorityDraft] = useState(undefined);

    // Project Type filter state
    const [filterProjectType, setFilterProjectType] = useState(undefined);
    const [filterProjectTypeDraft, setFilterProjectTypeDraft] = useState(undefined);

    // Template filter state
    const [filterTemplateId, setFilterTemplateId] = useState(undefined);
    const [filterTemplateIdDraft, setFilterTemplateIdDraft] = useState(undefined);

    // Category filter state
    const [filterCategoryId, setFilterCategoryId] = useState(undefined);
    const [filterCategoryIdDraft, setFilterCategoryIdDraft] = useState(undefined);

    // Client Type filter state
    const [filterClientType, setFilterClientType] = useState(undefined);
    const [filterClientTypeDraft, setFilterClientTypeDraft] = useState(undefined);

    // Client ID filter state
    const [filterClientId, setFilterClientId] = useState(undefined);
    const [filterClientIdDraft, setFilterClientIdDraft] = useState(undefined);

    const prevFilterModalOpenRef = useRef(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createInitialValues, setCreateInitialValues] = useState(null);

    // Fetch categories, templates, and clients for the filter dropdowns
    const categoriesQuery = useQuery({
        queryKey: ['public-categories-filter'],
        queryFn: getPublicCategories,
        staleTime: 10 * 60_000,
    });
    const categories = categoriesQuery.data ?? [];
    const categoryOptions = useMemo(() => categories.map(c => ({ value: c.id, label: c.name })), [categories]);

    const templatesQuery = useQuery({
        queryKey: ['project-templates-filter'],
        queryFn: getProjectTemplates,
        staleTime: 5 * 60_000,
    });
    const templates = useMemo(() => {
        const raw = templatesQuery.data;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.data)) return raw.data;
        return [];
    }, [templatesQuery.data]);
    const templateOptions = useMemo(() => templates.map(t => ({ value: t.id || t._id, label: t.name })), [templates]);

    const clientsQuery = useQuery({
        queryKey: ['clients-filter'],
        queryFn: () => getClients({ page: 1, limit: 100, sortBy: 'createdAt', sortOrder: 'desc', type: 'all' }),
        staleTime: 30_000,
    });
    const clientsList = clientsQuery.data?.data ?? [];
    const clientOptions = useMemo(() => clientsList.map((c) => {
        const name = c?.name ?? '';
        const email = c?.email ?? '';
        const base = name ? (email ? `${name} (${email})` : name) : email || c?.id;
        return { value: c?.id, label: base };
    }), [clientsList]);

    const triggerSearch = useCallback((val) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }
        setDebouncedSearch(val.trim());
    }, []);

    const handleSearchChange = useCallback((e) => {
        const val = e.target.value;
        setSearchText(val);

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(val.trim());
        }, 500);
    }, []);

    const handleSearchKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            triggerSearch(searchText);
        }
    }, [searchText, triggerSearch]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const listQueryParams = useMemo(
        () => buildProjectsListQueryParams({
            statusTab,
            STATUS_TAB,
            filterPriority,
            debouncedSearch,
            filterProjectType,
            filterTemplateId,
            filterCategoryId,
            filterClientType,
            filterClientId,
        }),
        [
            statusTab,
            filterPriority,
            debouncedSearch,
            filterProjectType,
            filterTemplateId,
            filterCategoryId,
            filterClientType,
            filterClientId,
        ],
    );

    const {
        data: listProjectsResponse,
        isRefetching: projectsListRefetching,
    } = useProjects(listQueryParams);
    const { data: kpiProjectsResponse } = useProjects(PROJECTS_KPI_QUERY_PARAMS);

    const refetchProjects = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
    }, [queryClient]);

    const projects = listProjectsResponse?.data ?? [];
    const kpiProjects = kpiProjectsResponse?.data ?? [];
    const total = kpiProjectsResponse?.total ?? 0;
    const activeCount = useMemo(
        () => kpiProjects.filter((p) => (p.status || '').toLowerCase() === 'active').length,
        [kpiProjects],
    );
    const withClientCount = useMemo(
        () => kpiProjects.filter((p) => p.client?.id).length,
        [kpiProjects],
    );
    const highPriorityCount = useMemo(
        () => kpiProjects.filter((p) => (p.priority || '').toLowerCase() === 'high').length,
        [kpiProjects],
    );

    const hasActiveFilters = Boolean(
        filterPriority ||
        filterProjectType ||
        filterTemplateId ||
        filterCategoryId ||
        filterClientType ||
        filterClientId
    );

    useEffect(() => {
        if (filterModalOpen && !prevFilterModalOpenRef.current) {
            setFilterPriorityDraft(filterPriority);
            setFilterProjectTypeDraft(filterProjectType);
            setFilterTemplateIdDraft(filterTemplateId);
            setFilterCategoryIdDraft(filterCategoryId);
            setFilterClientTypeDraft(filterClientType);
            setFilterClientIdDraft(filterClientId);
        }
        prevFilterModalOpenRef.current = filterModalOpen;
    }, [
        filterModalOpen,
        filterPriority,
        filterProjectType,
        filterTemplateId,
        filterCategoryId,
        filterClientType,
        filterClientId,
    ]);

    const clearFilterDraft = useCallback(() => {
        setFilterPriorityDraft(undefined);
        setFilterProjectTypeDraft(undefined);
        setFilterTemplateIdDraft(undefined);
        setFilterCategoryIdDraft(undefined);
        setFilterClientTypeDraft(undefined);
        setFilterClientIdDraft(undefined);
    }, []);

    const applyFiltersFromModal = useCallback(() => {
        setFilterPriority(filterPriorityDraft);
        setFilterProjectType(filterProjectTypeDraft);
        setFilterTemplateId(filterTemplateIdDraft);
        setFilterCategoryId(filterCategoryIdDraft);
        setFilterClientType(filterClientTypeDraft);
        setFilterClientId(filterClientIdDraft);
        setFilterModalOpen(false);
    }, [
        filterPriorityDraft,
        filterProjectTypeDraft,
        filterTemplateIdDraft,
        filterCategoryIdDraft,
        filterClientTypeDraft,
        filterClientIdDraft,
    ]);

    const clearFilters = useCallback(() => {
        setFilterPriority(undefined);
        setFilterPriorityDraft(undefined);
        setFilterProjectType(undefined);
        setFilterProjectTypeDraft(undefined);
        setFilterTemplateId(undefined);
        setFilterTemplateIdDraft(undefined);
        setFilterCategoryId(undefined);
        setFilterCategoryIdDraft(undefined);
        setFilterClientType(undefined);
        setFilterClientTypeDraft(undefined);
        setFilterClientId(undefined);
        setFilterClientIdDraft(undefined);
        setFilterModalOpen(false);
    }, []);


    const filteredProjects = useMemo(() => {
        return projects.filter((p) => {
            if (statusTab && statusTab !== STATUS_TAB.ALL) {
                if (statusTab === STATUS_TAB.STARRED) {
                    if (!p.isStarred) return false;
                } else {
                    const projectStatus = (p.status || '').toLowerCase();
                    const targetStatus = statusTab.toLowerCase();
                    if (projectStatus !== targetStatus) {
                        return false;
                    }
                }
            }

            const query = debouncedSearch.toLowerCase().trim();
            if (query) {
                const searchableFields = [
                    p.name,
                    p.slug,
                    p.clientName,
                    p.clientEmail,
                    p.clientPhone,
                    p.client?.name,
                    p.client?.email,
                    p.client?.clientUserId,
                    p.client?.clientUser?.name,
                    p.client?.clientUser?.email,
                    p.categoryName,
                    p.category?.name,
                    p.template?.name,
                    p.template?.code,
                    p.template?.description,
                    p.projectTemplate?.name,
                    p.projectTemplate?.code,
                    p.projectTemplate?.description,
                ];

                const isMatch = searchableFields.some((field) =>
                    String(field ?? '').toLowerCase().includes(query)
                );

                if (!isMatch) {
                    return false;
                }
            }

            return true;
        });
    }, [projects, statusTab, debouncedSearch, STATUS_TAB]);

    const tableData = useMemo(
        () =>
            (statusTab === STATUS_TAB.STARRED
                ? [...filteredProjects].sort((a, b) => (a.starredSortOrder ?? 0) - (b.starredSortOrder ?? 0))
                : statusTab === STATUS_TAB.ALL
                    ? [...filteredProjects].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                    : filteredProjects
            ).map((p) => ({
                key: p.id,
                id: p.id,
                name: p.name,
                client: p.client,
                budget: p.budget,
                priority: p.priority,
                status: p.status,
                assignedUsers: p.assignedUsers,
                capabilities: p.capabilities,
                categoryName: p.categoryName,
                isStarred: p.isStarred,
                starredSortOrder: p.starredSortOrder,
                sortOrder: p.sortOrder,
            })),
        [filteredProjects, statusTab],
    );

    const closeCreateModal = useCallback(() => {
        setCreateModalOpen(false);
        setCreateInitialValues(null);
    }, []);

    useEffect(() => {
        if (location.state?.openCreateModal) {
            setCreateModalOpen(true);
            if (location.state.clientName || location.state.clientEmail || location.state.clientPhone) {
                setCreateInitialValues({
                    clientName: location.state.clientName,
                    clientEmail: location.state.clientEmail,
                    clientPhone: location.state.clientPhone,
                });
            }
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const openProjectDetail = useCallback(
        (id, name) => {
            if (!id) return;
            const shellTitle = typeof name === 'string' ? name.trim() : '';
            navigate(projectDetailPath(id, location.pathname), {
                state: shellTitle ? { projectShellTitle: shellTitle } : undefined,
            });
        },
        [navigate, location.pathname],
    );

    const getProjectStatus = (record) => {
        const status = record?.status?.toLowerCase();

        switch (status) {
            case 'pending_completion':
                return 'Pending';
            default:
                return status;
        }
    };
    const columns = useMemo(
        () => [
            {
                title: 'Project',
                key: 'name',
                width: '250px',
                render: (_, record) => (
                    <div>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--ant-color-text)' }}>
                            {record.name}
                        </span>
                        {record.categoryName && (
                            <span style={{ display: 'block', fontSize: '12px', color: 'var(--ant-color-text-secondary)', marginTop: '2px' }}>
                                {record.categoryName}
                            </span>
                        )}
                    </div>
                ),
            },

            ...(canViewClients
                ? [
                    {
                        title: 'Client',
                        key: 'client',
                        width: '200px',
                        render: (_, record) => (
                            <div>
                                <span style={{ fontWeight: 500, color: 'var(--ant-color-text)' }}>
                                    {record.client?.name ?? '—'}
                                </span>
                                {record.budget != null && (
                                    <span style={{ display: 'block', fontSize: '12px', color: 'var(--ant-color-text-secondary)', marginTop: '2px' }}>
                                        {asCurrency(record.budget)}
                                    </span>
                                )}
                            </div>
                        ),
                    },
                ]
                : []),
            {
                title: 'Priority',
                dataIndex: 'priority',
                key: 'priority',
                width: '150px',
                render: (val) => capFirst(val),
            },
            {
                title: 'Type',
                key: 'type',
                width: '150px',
                render: (_, record) => {
                    const t = record.capabilities?.projectType;
                    if (!t) return '—';
                    if (t === 'fixed_sdlc') return 'Fixed SDLC';
                    if (t === 'retainer') return 'Retainer';
                    return toTitleWords(t);
                },
            },
            {
                title: 'Capabilities',
                key: 'capabilities',
                width: '200px',
                render: (_, record) => {
                    const caps = [];
                    const capObj = record.capabilities ?? {};
                    if (capObj.usePhases) caps.push({ label: 'Phase', color: 'blue' });
                    if (capObj.useMilestones) caps.push({ label: 'Milestone', color: 'green' });
                    if (capObj.useSprints) caps.push({ label: 'Sprint', color: 'gold' });
                    if (capObj.useTasks) caps.push({ label: 'Task', color: 'purple' });
                    if (capObj.useTimeLogs) caps.push({ label: 'Time Log', color: 'cyan' });
                    if (capObj.clientSubmissionForApproval) caps.push({ label: 'Client Submission Approval', color: 'red' });

                    return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {caps.length === 0 ? (
                                <span style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)' }}>—</span>
                            ) : (
                                caps.map((c) => (
                                    <Tag key={c.label} color={c.color} bordered={false} style={{ fontSize: '11px', fontWeight: 500 }}>
                                        {c.label}
                                    </Tag>
                                ))
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: '250px',
                render: (_, record) => <StatusBadge status={getProjectStatus(record)} />,
            },
            {
                title: 'Assignee',
                key: 'assigned',
                render: (_, record) => {
                    const list = record.assignedUsers ?? [];
                    if (list.length === 0) return '—';
                    const maxShow = 4;
                    const show = list.slice(0, maxShow);
                    const overflowCount = list.length - maxShow;
                    return (
                        <AvatarStackWrap>
                            {show.map((u) => (
                                <Tooltip key={u.id} title={u.name || u.email}>
                                    <AvatarStackCircle $bg={getAvatarColor(u.id)} $color="#fff">
                                        {u.profileImage ? (
                                            <img
                                                src={u.profileImage}
                                                alt=""
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: '50%',
                                                }}
                                            />
                                        ) : (
                                            getInitials(u.name || u.email)
                                        )}
                                    </AvatarStackCircle>
                                </Tooltip>
                            ))}
                            {overflowCount > 0 && (
                                <Tooltip title={`${overflowCount} more`}>
                                    <AvatarStackOverflow>+{overflowCount}</AvatarStackOverflow>
                                </Tooltip>
                            )}
                        </AvatarStackWrap>
                    );
                },
            },
        ],
        [],
    );

    return {
        searchText,
        setSearchText,
        handleSearchChange,
        handleSearchKeyDown,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        openProjectDetail,
        createModalOpen,
        setCreateModalOpen,
        createInitialValues,
        total,
        activeCount,
        withClientCount,
        highPriorityCount,
        tableData,
        columns,
        closeCreateModal,
        PRIORITY_OPTIONS: PRIORITY_OPTIONS_FOR_FILTER,
        PROJECT_TYPE_OPTIONS,
        CLIENT_TYPE_OPTIONS,
        categoryOptions,
        templateOptions,
        clientOptions,
        refetchProjects,
        projectsListRefetching,
        filterModalOpen,
        setFilterModalOpen,
        filterPriority,
        filterPriorityDraft,
        setFilterPriorityDraft,
        filterProjectTypeDraft,
        setFilterProjectTypeDraft,
        filterTemplateIdDraft,
        setFilterTemplateIdDraft,
        filterCategoryIdDraft,
        setFilterCategoryIdDraft,
        filterClientTypeDraft,
        setFilterClientTypeDraft,
        filterClientIdDraft,
        setFilterClientIdDraft,
        hasActiveFilters,
        clearFilterDraft,
        applyFiltersFromModal,
        clearFilters,
        handleReorderStarredProjects: useCallback((projectIds) => {
            queryClient.setQueryData([...PROJECTS_QUERY_KEY, userId, listQueryParams], (oldData) => {
                if (!oldData) return oldData;
                const projectsList = oldData.data || [];
                const reordered = [...projectsList].sort((a, b) => {
                    const indexA = projectIds.indexOf(a.id);
                    const indexB = projectIds.indexOf(b.id);
                    if (indexA !== -1 && indexB !== -1) {
                        return indexA - indexB;
                    }
                    return 0;
                });
                reordered.forEach((p, i) => {
                    if (projectIds.includes(p.id)) {
                        p.starredSortOrder = i + 1;
                    }
                });
                return {
                    ...oldData,
                    data: reordered,
                };
            });
            reorderStarredProjects(projectIds).catch(() => {
                queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
            });
        }, [queryClient, userId, listQueryParams]),
        handleReorderProjects: useCallback((projectIds) => {
            queryClient.setQueryData([...PROJECTS_QUERY_KEY, userId, listQueryParams], (oldData) => {
                if (!oldData) return oldData;
                const projectsList = oldData.data || [];
                const reordered = [...projectsList].sort((a, b) => {
                    const indexA = projectIds.indexOf(a.id);
                    const indexB = projectIds.indexOf(b.id);
                    if (indexA !== -1 && indexB !== -1) {
                        return indexA - indexB;
                    }
                    return 0;
                });
                reordered.forEach((p, i) => {
                    if (projectIds.includes(p.id)) {
                        p.sortOrder = i + 1;
                    }
                });
                return {
                    ...oldData,
                    data: reordered,
                };
            });
            reorderProjects(projectIds).catch(() => {
                queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
            });
        }, [queryClient, userId, listQueryParams]),
        handleToggleStar: useCallback((id, isStarred) => {
            updateProjectSettings(id, { isStarred })
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
                    message.success(isStarred ? 'Project starred successfully' : 'Project unstarred successfully');
                })
                .catch((err) => {
                    message.error(err?.response?.data?.message || err?.message || 'Failed to update project');
                });
        }, [queryClient]),
    };
};
